/**
 * Core map lifecycle hook — handles creation, destruction, and mode switching.
 * Manages the Google Maps instance (2D or 3D) based on store viewMode.
 */

import { useEffect, useRef, useCallback } from 'react';
import { createMap2D, createMap3D, clearContainer, applyLabelMode } from '@/map/map-setup';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { setupMarkers, destroyMarkers } from '@/map/markers';
import { destroyTracks } from '@/map/track-history';
import { destroyShapes } from '@/map/shapes';
import { clearDrawOverlay } from '@/map/drawing/draw-overlay';
import { useMapStore } from '@/stores/map-store';

/** Return type for the lifecycle hook */
interface MapLifecycle {
  mapRef: React.RefObject<MapInstance | null>;
  modeRef: React.RefObject<ViewMode>;
}

/** Manages Google Maps creation/destruction and mode switching */
export function useMapLifecycle(containerRef: React.RefObject<HTMLDivElement | null>): MapLifecycle {
  const mapRef = useRef<MapInstance | null>(null);
  const modeRef = useRef<ViewMode>('3d');
  const initializingRef = useRef(false);

  const initMap = useCallback(async (container: HTMLElement, mode: ViewMode) => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    try {
      destroyCurrentMap(mapRef.current, modeRef.current, container);

      const map = mode === '2d'
        ? await createMap2D(container)
        : await createMap3D(container);

      mapRef.current = map;
      modeRef.current = mode;
      if (mode === '3d') {
        applyLabelMode(map as google.maps.maps3d.Map3DElement, useMapStore.getState().labelsVisible);
      }
      setupMarkers(map, mode);
      schedulePaintFix();
    } finally {
      initializingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const currentMode = useMapStore.getState().viewMode;
    void initMap(container, currentMode);

    const unsubMode = useMapStore.subscribe((state, prev) => {
      if (state.viewMode !== prev.viewMode && container) {
        void initMap(container, state.viewMode);
      }
    });

    return () => {
      unsubMode();
      destroyCurrentMap(mapRef.current, modeRef.current, container);
      mapRef.current = null;
    };
  }, []);

  return { mapRef, modeRef };
}

/**
 * Force a 2D map repaint after creation. Google Maps can render blank when the
 * map is created before its container has been laid out (common during slow
 * cold loads); a resize event makes it re-measure and paint.
 */
function schedulePaintFix(): void {
  requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
}

/** Clean up the current map instance and clear the container for mode switch */
function destroyCurrentMap(
  map: MapInstance | null,
  mode: ViewMode,
  container: HTMLElement,
): void {
  if (!map) return;

  destroyMarkers(mode);
  destroyTracks(mode);
  destroyShapes(mode);
  clearDrawOverlay(mode);

  // Clear ALL children — both 2D (Google injects divs) and 3D (gmp-map-3d element)
  clearContainer(container);
}
